import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { General } from 'icons/untitledui';

import useDropdownDirection from 'shared/hooks/useDropdownDirection';
import * as Styled from 'theme/components/SelectionDropdown.style';
import {
  BaseSelectionDropdown,
  type BaseDropdownOption,
} from './BaseSelectionDropdown';

export type SelectionDropdownOption = {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
};

interface SelectionDropdownProps {
  ariaLabel: string;
  value: string | null | undefined;
  options: SelectionDropdownOption[];
  onChange: (value: string) => void;
  placeholder?: React.ReactNode;
  /** When true, the trigger + popover fill the available width. */
  fullWidth?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function SelectionDropdown({
  ariaLabel,
  value,
  options,
  onChange,
  placeholder = 'Select',
  fullWidth = false,
  isOpen: controlledOpen,
  onOpenChange: externalOnOpenChange,
}: SelectionDropdownProps): JSX.Element {
  const [alignRight, setAlignRight] = useState(false);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const { openUp } = useDropdownDirection({ open, triggerRef });

  const onOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      externalOnOpenChange?.(next);
    },
    [externalOnOpenChange]
  );

  const selectedOption = useMemo(() => {
    if (value == null) return null;
    return options.find((o) => o.value === value) ?? null;
  }, [options, value]);

  useEffect(() => {
    if (!open) return undefined;
    if (fullWidth) {
      setAlignRight(false);
      return undefined;
    }

    const POPUP_WIDTH = 260;
    const EDGE_PADDING = 16;

    const compute = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const overflowRightIfLeftAligned =
        rect.left + POPUP_WIDTH > window.innerWidth - EDGE_PADDING;
      const overflowLeftIfRightAligned =
        rect.right - POPUP_WIDTH < EDGE_PADDING;

      if (overflowRightIfLeftAligned && !overflowLeftIfRightAligned) {
        setAlignRight(true);
        return;
      }
      if (!overflowRightIfLeftAligned && overflowLeftIfRightAligned) {
        setAlignRight(false);
        return;
      }
      if (overflowRightIfLeftAligned && overflowLeftIfRightAligned) {
        const overflowRightAmount =
          rect.left + POPUP_WIDTH - (window.innerWidth - EDGE_PADDING);
        const overflowLeftAmount = EDGE_PADDING - (rect.right - POPUP_WIDTH);
        setAlignRight(overflowRightAmount > overflowLeftAmount);
        return;
      }
      setAlignRight(false);
    };

    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [fullWidth, open]);

  const isSelected = useCallback(
    (opt: BaseDropdownOption) => opt.value === value,
    [value]
  );

  const onOptionSelect = useCallback(
    (opt: BaseDropdownOption) => onChange(opt.value),
    [onChange]
  );

  const renderOptionCheck = useCallback(
    (selected: boolean) =>
      selected ? (
        <Styled.OptionCheck>
          <General.Check size={12} />
        </Styled.OptionCheck>
      ) : null,
    []
  );

  const onOpenEffect = useCallback(
    (listRef: React.RefObject<HTMLDivElement | null>) => {
      const list = listRef.current;
      if (!list) return;
      const el = list.querySelector(
        `[data-selection-option="true"][data-selected="true"]`
      ) as HTMLElement | null;
      if (!el) return;
      const listRect = list.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      list.scrollTop +=
        elRect.top - listRect.top - (listRect.height - elRect.height) / 2;
    },
    []
  );

  const renderPopover = useCallback(
    ({
      listRef,
      ariaLabel: label,
      optionElements,
    }: {
      listRef: React.RefObject<HTMLDivElement | null>;
      ariaLabel: string;
      optionElements: React.ReactNode;
    }) => (
      <Styled.Popover
        role="listbox"
        aria-label={label}
        $alignRight={alignRight}
        $fullWidth={fullWidth}
        $openUp={openUp}
      >
        <Styled.List ref={listRef}>{optionElements}</Styled.List>
      </Styled.Popover>
    ),
    [alignRight, fullWidth, openUp]
  );

  return (
    <BaseSelectionDropdown
      ariaLabel={ariaLabel}
      options={options}
      displayValue={selectedOption?.label ?? placeholder}
      isOpen={controlledOpen}
      onOpenChange={onOpenChange}
      triggerRef={triggerRef}
      triggerStyleProps={{ $fullWidth: fullWidth }}
      isSelected={isSelected}
      onOptionSelect={onOptionSelect}
      renderOptionCheck={renderOptionCheck}
      renderPopover={renderPopover}
      onOpenEffect={onOpenEffect}
    />
  );
}
